import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, AlertTriangle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const AdminPricing = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    base: true,
    clamp: false,
    tonnage: false,
    distance: false,
    marchandise: false,
    conditionnement: false,
    marge: false
  });
  const [showResetDialog, setShowResetDialog] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/pricing-admin/config');
      setConfig(response.data.config);
    } catch (err) {
      toast.error('Erreur chargement configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/pricing-admin/config', { config_data: config });
      toast.success('Configuration sauvegardée avec succès !');
    } catch (err) {
      toast.error('Erreur sauvegarde configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      await api.post('/pricing-admin/config/reset');
      toast.success('Configuration réinitialisée ! Redémarrez le serveur.');
      setShowResetDialog(false);
      fetchConfig();
    } catch (err) {
      toast.error('Erreur réinitialisation');
    }
  };

  const updateNestedValue = (path, value) => {
    const keys = path.split('.');
    const newConfig = { ...config };
    let current = newConfig;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = parseFloat(value) || value;
    setConfig(newConfig);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const AccordionSection = ({ title, isExpanded, onToggle, children }) => (
    <div className="bg-white rounded-lg shadow mb-3">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200 pt-4">
          {children}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Configuration non disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-8 h-8 text-emerald-600" />
              <h1 className="text-3xl font-bold text-gray-900">Configuration Pricing</h1>
            </div>
            <p className="text-gray-600">Gérez les coefficients et paramètres du moteur de tarification</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchConfig}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Recharger
            </button>
            <button
              onClick={() => setShowResetDialog(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Base P_tkm */}
        <AccordionSection 
          title="Base P_tkm (FCFA/t·km)"
          isExpanded={expandedSections.base}
          onToggle={() => toggleSection('base')}
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domestique CI</label>
              <input
                type="number"
                value={config.base_p_tkm?.domestique_ci || 0}
                onChange={(e) => updateNestedValue('base_p_tkm.domestique_ci', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CI → Sahel</label>
              <input
                type="number"
                value={config.base_p_tkm?.ci_vers_sahel || 0}
                onChange={(e) => updateNestedValue('base_p_tkm.ci_vers_sahel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </AccordionSection>

        {/* Clamp P_tkm */}
        <AccordionSection 
          title="Limites P_tkm"
          isExpanded={expandedSections.clamp}
          onToggle={() => toggleSection('clamp')}
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum</label>
              <input
                type="number"
                value={config.p_tkm_clamp?.min || 0}
                onChange={(e) => updateNestedValue('p_tkm_clamp.min', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Maximum</label>
              <input
                type="number"
                value={config.p_tkm_clamp?.max || 0}
                onChange={(e) => updateNestedValue('p_tkm_clamp.max', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </AccordionSection>

        {/* Coefficients Tonnage */}
        <AccordionSection 
          title="Coefficients Tonnage"
          isExpanded={expandedSections.tonnage}
          onToggle={() => toggleSection('tonnage')}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {config.coeff_tonnage && Object.entries(config.coeff_tonnage).map(([seuil, coeff]) => (
              <div key={seuil}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{'≤'} {seuil}T</label>
                <input
                  type="number"
                  value={coeff}
                  onChange={(e) => updateNestedValue(`coeff_tonnage.${seuil}`, e.target.value)}
                  step="0.05"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            ))}
          </div>
        </AccordionSection>

        {/* Coefficients Distance */}
        <AccordionSection 
          title="Coefficients Distance"
          isExpanded={expandedSections.distance}
          onToggle={() => toggleSection('distance')}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {config.coeff_distance && Object.entries(config.coeff_distance).map(([seuil, coeff]) => (
              <div key={seuil}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{'<'} {seuil} km</label>
                <input
                  type="number"
                  value={coeff}
                  onChange={(e) => updateNestedValue(`coeff_distance.${seuil}`, e.target.value)}
                  step="0.05"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            ))}
          </div>
        </AccordionSection>

        {/* Coefficients Marchandise */}
        <AccordionSection 
          title="Coefficients Marchandise"
          isExpanded={expandedSections.marchandise}
          onToggle={() => toggleSection('marchandise')}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {config.coeff_marchandise && Object.entries(config.coeff_marchandise).map(([type, coeff]) => (
              <div key={type}>
                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{type}</label>
                <input
                  type="number"
                  value={coeff}
                  onChange={(e) => updateNestedValue(`coeff_marchandise.${type}`, e.target.value)}
                  step="0.05"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            ))}
          </div>
        </AccordionSection>

        {/* Coefficients Conditionnement */}
        <AccordionSection 
          title="Coefficients Conditionnement"
          isExpanded={expandedSections.conditionnement}
          onToggle={() => toggleSection('conditionnement')}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {config.coeff_conditionnement && Object.entries(config.coeff_conditionnement).map(([type, coeff]) => (
              <div key={type}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{type.replace('_', ' ')}</label>
                <input
                  type="number"
                  value={coeff}
                  onChange={(e) => updateNestedValue(`coeff_conditionnement.${type}`, e.target.value)}
                  step="0.02"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            ))}
          </div>
        </AccordionSection>

        {/* Marge Minimale */}
        <AccordionSection 
          title="Marge Minimale par Défaut"
          isExpanded={expandedSections.marge}
          onToggle={() => toggleSection('marge')}
        >
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">Marge minimale (%)</label>
            <input
              type="number"
              value={(config.marge_minimale_default || 0) * 100}
              onChange={(e) => updateNestedValue('marge_minimale_default', parseFloat(e.target.value) / 100)}
              step="1"
              min="0"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </AccordionSection>

        {/* Bouton Sauvegarder */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-6 rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Sauvegarder la configuration
              </>
            )}
          </button>
        </div>

        {/* Dialog Reset */}
        {showResetDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold mb-4">Réinitialiser la configuration ?</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 text-sm">
                  Cette action réinitialisera tous les paramètres aux valeurs par défaut.
                  Un redémarrage du serveur sera nécessaire pour appliquer les changements.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowResetDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPricing;